import { CopyOutlined, DeleteOutlined, DragOutlined, FilterOutlined } from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Badge, Button, Input, Select, Switch, Tooltip } from "antd";
import classNames from "classnames";
import { PingtreeFormData } from "components/Forms/PingtreeForm/formSchema.ts";
import { CascadeFilter } from "components/other/CascadeFilter/CascadeFilter.tsx";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { IntegrationSelectSearchable } from "components/UI/Selects/IntegrationSelectSearchable/IntegrationSelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { memo } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { DrawerId } from "types/models.ts";
import { zIndexes } from "utils/zIndexes.ts";

export interface CascadeItemProps {
    id: string;
    deletedIds: Array<number | string>;
    visibleCascadeFilters: Set<string>;
    distributionOptions: Array<{ label: string; value: string | number }>;
    actionOptions: Array<{ label: string; value: string | number }>;
    getCascadeFilterCount: (id: string) => number;
    toggleCascadeFilter: (id: string) => void;
    handleDeleteCascade: (id: string) => void;
}

export const CascadeItem = memo(
    ({
        id,
        deletedIds,
        visibleCascadeFilters,
        distributionOptions,
        actionOptions,
        getCascadeFilterCount,
        toggleCascadeFilter,
        handleDeleteCascade,
    }: CascadeItemProps) => {
        const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
        const { t } = usePaldockTranslation("common");

        const handleCopyId = async () => {
            try {
                await navigator.clipboard.writeText(id);
                toast.success(t("copied_to_clipboard"));
            } catch {
                toast.error(t("errors.something_went_wrong"));
            }
        };

        const { control } = useGeneralForm<PingtreeFormData>();

        const cascadeCommissionsDrawerId = DrawerId.CascadeCommissions;
        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
        };

        const isDeleted = deletedIds.includes(id);
        const fieldPrefix = `channels.${id}` as const;

        return (
            <li
                className={classNames(" bg-gray-50 rounded-lg", isDeleted && "opacity-50")}
                ref={setNodeRef}
                style={style}
                {...attributes}
                key={id}
            >
                <div
                    className={
                        "flex flex-row justify-between font-bold gap-x-4 items-center w-full py-2 pr-2 pl-11 relative"
                    }
                >
                    <div
                        className={`absolute -left-3 top-1/2 -translate-y-1/2 z-${zIndexes.cascadeButtons} flex flex-row gap-1`}
                    >
                        <Button className="w-6 h-6 rounded-md p-0" type="primary" {...listeners}>
                            <DragOutlined />
                        </Button>
                        {!id.startsWith("tmp") && (
                            <Tooltip title={id}>
                                <Button
                                    className="w-6 h-6 rounded-md p-0"
                                    type="primary"
                                    onClick={handleCopyId}
                                    aria-label={t("copy")}
                                >
                                    <CopyOutlined />
                                </Button>
                            </Tooltip>
                        )}
                    </div>

                    <div className="grid grow grid-cols-5 gap-x-4">
                        <FormItem control={control} name={`${fieldPrefix}.title`} className="w-full !mb-0" required>
                            <Input />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${fieldPrefix}.visible_title`}
                            className="w-full !mb-0"
                            required
                        >
                            <Input />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${fieldPrefix}.integration`}
                            className="w-full !mb-0"
                            required={!isDeleted}
                        >
                            <IntegrationSelectSearchable />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${fieldPrefix}.distribution`}
                            className="w-full !mb-0"
                            required={!isDeleted}
                        >
                            <Select options={distributionOptions} />
                        </FormItem>
                        <FormItem
                            control={control}
                            name={`${fieldPrefix}.action`}
                            className="w-full !mb-0"
                            required={!isDeleted}
                        >
                            <Select options={actionOptions} />
                        </FormItem>
                    </div>

                    <div className="grid shrink-0 w-80 grid-cols-4 gap-4 items-center">
                        <>
                            {id && !id.startsWith("tmp") ? (
                                <DrawerLink
                                    drawerId={cascadeCommissionsDrawerId}
                                    search={{
                                        [`${cascadeCommissionsDrawerId}-id`]: id ?? "",
                                    }}
                                    action="show"
                                >
                                    <Button type="primary">{t("view")}</Button>
                                </DrawerLink>
                            ) : (
                                <Button type="primary" disabled={true}>
                                    {t("view")}
                                </Button>
                            )}
                        </>
                        <Badge count={getCascadeFilterCount(id)} color="#008080">
                            <Button
                                icon={<FilterOutlined />}
                                className={classNames(
                                    "!w-11 shrink-0 border-0",
                                    visibleCascadeFilters.has(id) ? "bg-blue-100" : "bg-transparent"
                                )}
                                onClick={() => toggleCascadeFilter(id)}
                            ></Button>
                        </Badge>
                        <FormItem control={control} name={`${fieldPrefix}.active`} className="!mb-0">
                            <Switch />
                        </FormItem>
                        <Button
                            icon={<DeleteOutlined />}
                            className="!w-11 shrink-0 border-0 bg-transparent"
                            onClick={() => handleDeleteCascade(id)}
                        ></Button>
                    </div>
                </div>

                {/* Show cascade filter below the item if visible */}
                {visibleCascadeFilters.has(id) && (
                    <div className="mt-2 px-4">
                        <CascadeFilter cascadeId={id} />
                    </div>
                )}
            </li>
        );
    }
);

CascadeItem.displayName = "CascadeItem";
